'use client'

/**
 * Generador de la carta de respuesta del Ratón Pérez.
 *
 * Sustituye la plantilla estática con huecos `____`, que obligaba a imprimir y
 * escribir a mano. El padre rellena cuatro campos y se lleva la carta hecha.
 *
 * Privacidad: no hay ni una petición de red en todo el flujo. El nombre del
 * niño se queda en el dispositivo; el PNG se compone en un canvas local y la
 * analítica solo registra QUÉ campos se usaron, nunca su contenido.
 */

import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  composeToothLetter,
  letterFilename,
  type ComposedLetter,
  type ToothKind,
} from '@/lib/guides/tools/tooth-letter'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import { ComposedLetterSheet } from './composed-letter-sheet'
import { CallBridge } from './call-bridge'

const COPY = {
  heading: 'Escribe la carta del Ratón Pérez en un minuto',
  intro:
    'Rellena lo que sepas — solo el nombre es imprescindible — y te la damos lista para guardar o imprimir. Nada de esto sale de tu móvil.',
  name: 'Nombre del niño o la niña',
  namePlaceholder: 'Lucía',
  age: 'Edad (opcional)',
  tooth: '¿Qué diente es?',
  howItFell: '¿Cómo se le cayó? (opcional)',
  howItFellPlaceholder: 'comiendo una manzana',
  achievement: 'Algo que haya conseguido (opcional)',
  achievementPlaceholder: 'ha aprendido a nadar',
  amount: '¿Qué le dejas? (opcional)',
  amountPlaceholder: '2 €',
  generate: 'Escribir la carta',
  regenerate: 'Actualizar la carta',
  nameRequired: 'Pon al menos el nombre y el ratón se encarga del resto.',
  sheetTitle: 'Carta del Ratón Pérez',
  ctaTitle: 'El Ratón Pérez ya sabe de {name}. ¿Se lo dice en persona?',
  ctaBody:
    'Puede llamarle esta noche por videollamada: le felicita por su valentía, le llama por su nombre y le recuerda que se cepille.',
  ctaAction: 'Preparar la llamada del Ratón Pérez',
}

export const TOOTH_OPTIONS: Array<{ value: ToothKind; label: string }> = [
  { value: 'first', label: 'El primero' },
  { value: 'next', label: 'Otro diente' },
  { value: 'molar', label: 'Una muela' },
]

const ANALYTICS_BASE = {
  character_id: 'raton-perez',
  guide_slug: 'carta-al-raton-perez',
  locale: 'es',
}

type FormState = {
  childName: string
  age: string
  tooth: ToothKind
  howItFell: string
  achievement: string
  amount: string
}

const EMPTY_FORM: FormState = {
  childName: '',
  age: '',
  tooth: 'first',
  howItFell: '',
  achievement: '',
  amount: '',
}

/** Selector de tipo de diente como píldoras: más cómodo en móvil que un select. */
export function ToothPicker({
  value,
  onChange,
  legend,
  name,
}: {
  value: ToothKind
  onChange: (tooth: ToothKind) => void
  legend: string
  name: string
}) {
  return (
    <fieldset>
      <legend className="text-sm text-white/80">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {TOOTH_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`cursor-pointer rounded-full border px-4 py-2 text-sm transition ${
              value === option.value
                ? 'border-white bg-white text-slate-900'
                : 'border-white/25 text-white/80 hover:border-white/50'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function LetterForm({
  form,
  onChange,
  onSubmit,
  hasLetter,
  showNameError,
}: {
  form: FormState
  onChange: (patch: Partial<FormState>) => void
  onSubmit: (event: React.FormEvent) => void
  hasLetter: boolean
  showNameError: boolean
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="letter-child-name">{COPY.name}</Label>
          <Input
            id="letter-child-name"
            value={form.childName}
            onChange={(event) => onChange({ childName: event.target.value })}
            placeholder={COPY.namePlaceholder}
            autoComplete="off"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="letter-child-age">{COPY.age}</Label>
          {/*
            Sin `min`/`max`: son restricciones nativas, y con ellas el navegador
            bloquea el envío del formulario entero por un campo OPCIONAL — te
            quedas sin carta por una edad mal escrita. El rango se aplica en
            `handleSubmit`, que descarta una edad imposible y sigue adelante.
          */}
          <Input
            id="letter-child-age"
            type="number"
            inputMode="numeric"
            value={form.age}
            onChange={(event) => onChange({ age: event.target.value })}
            className="mt-1"
          />
        </div>
      </div>

      <ToothPicker
        value={form.tooth}
        onChange={(tooth) => onChange({ tooth })}
        legend={COPY.tooth}
        name="letter-tooth"
      />

      <div>
        <Label htmlFor="letter-how-it-fell">{COPY.howItFell}</Label>
        <Input
          id="letter-how-it-fell"
          value={form.howItFell}
          onChange={(event) => onChange({ howItFell: event.target.value })}
          placeholder={COPY.howItFellPlaceholder}
          className="mt-1"
        />
      </div>
      <div>
        <Label htmlFor="letter-achievement">{COPY.achievement}</Label>
        <Input
          id="letter-achievement"
          value={form.achievement}
          onChange={(event) => onChange({ achievement: event.target.value })}
          placeholder={COPY.achievementPlaceholder}
          className="mt-1"
        />
      </div>
      <div>
        <Label htmlFor="letter-amount">{COPY.amount}</Label>
        <Input
          id="letter-amount"
          value={form.amount}
          onChange={(event) => onChange({ amount: event.target.value })}
          placeholder={COPY.amountPlaceholder}
          className="mt-1"
        />
      </div>

      {showNameError && <p className="text-sm text-red-300">{COPY.nameRequired}</p>}

      <Button
        type="submit"
        className="rounded-full bg-white px-6 py-5 text-base font-semibold text-slate-900 hover:bg-white/90"
      >
        <Sparkles className="size-4" aria-hidden />
        {hasLetter ? COPY.regenerate : COPY.generate}
      </Button>
    </form>
  )
}

/** Una edad fuera de rango se descarta: el campo es opcional y no debe bloquear. */
function parseOptionalAge(raw: string): number | undefined {
  const parsed = Number.parseInt(raw, 10)
  return Number.isInteger(parsed) && parsed > 0 && parsed < 15 ? parsed : undefined
}

export function ToothLetterGenerator() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [letter, setLetter] = useState<ComposedLetter | null>(null)
  const [childName, setChildName] = useState('')
  const [showNameError, setShowNameError] = useState(false)

  function handleChange(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }))
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const name = form.childName.trim()
    if (!name) {
      setShowNameError(true)
      setLetter(null)
      return
    }
    setShowNameError(false)
    const age = parseOptionalAge(form.age)

    setLetter(
      composeToothLetter({
        childName: name,
        age,
        tooth: form.tooth,
        howItFell: form.howItFell,
        achievement: form.achievement,
        amount: form.amount,
      })
    )
    setChildName(name)
    captureProductEvent(PRODUCT_EVENTS.GUIDE_LETTER_GENERATED, {
      ...ANALYTICS_BASE,
      tooth: form.tooth,
      // Qué campos opcionales usa la gente, sin mandar su contenido.
      has_age: age !== undefined,
      has_how_it_fell: Boolean(form.howItFell.trim()),
      has_achievement: Boolean(form.achievement.trim()),
      has_amount: Boolean(form.amount.trim()),
    })
  }

  return (
    <div
      data-testid="tooth-letter-generator"
      className="space-y-5 rounded-3xl border border-white/15 bg-white/5 p-6 md:p-8"
    >
      <div className="print:hidden">
        <h3 className="font-christmas text-xl font-bold text-white md:text-2xl">{COPY.heading}</h3>
        <p className="mt-2 text-base text-white/80">{COPY.intro}</p>
      </div>

      <div className="print:hidden">
        <LetterForm
          form={form}
          onChange={handleChange}
          onSubmit={handleSubmit}
          hasLetter={letter !== null}
          showNameError={showNameError}
        />
      </div>

      {letter && (
        <div className="space-y-4">
          <ComposedLetterSheet
            letter={letter}
            sheetTitle={COPY.sheetTitle}
            filename={letterFilename()}
            analyticsProperties={ANALYTICS_BASE}
          />
          <div className="print:hidden">
            <CallBridge
              childName={childName}
              character="raton-perez"
              locale="es"
              title={COPY.ctaTitle}
              body={COPY.ctaBody}
              action={COPY.ctaAction}
              placement="letter_generator"
              analyticsProperties={ANALYTICS_BASE}
            />
          </div>
        </div>
      )}
    </div>
  )
}
